import { useState, useMemo, useRef } from 'react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';
import { es } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBookings } from '@/hooks/useBookings';
import { useBatchedAvailability } from '@/hooks/useBatchedAvailability';
import { useBlockDate } from '@/hooks/useBlockDate';
import CalendarHeader from './CalendarHeader';
import CalendarDayHeaders from './CalendarDayHeaders';
import PropertyLabelsColumn from './PropertyLabelsColumn';
import CalendarGrid from './CalendarGrid';
import ReservationDetailModal from './ReservationDetailModal';
import BookingFormModal from '../BookingFormModal';
import { TooltipProvider } from '@/components/ui/tooltip';

interface Property {
  id: string;
  title: string;
  shortName?: string;
}

interface MultiPropertyCalendarProps {
  properties: Property[];
  isAdminContext?: boolean;
}

const MultiPropertyCalendar = ({ properties, isAdminContext = false }: MultiPropertyCalendarProps) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showBookingFormModal, setShowBookingFormModal] = useState(false);
  const [selectedPropertyForReservation, setSelectedPropertyForReservation] = useState<string>('');
  const [selectedDateForReservation, setSelectedDateForReservation] = useState<Date | null>(null);
  
  // Referencias para el área scrolleable
  const scrollableAreaRef = useRef<HTMLDivElement>(null);

  console.log('🔧 MultiPropertyCalendar render:', { 
    isAdminContext, 
    propertiesCount: properties.length,
    currentMonth: currentMonth.toDateString()
  });

  const propertyIds = properties.map(p => p.id);
  const { data: bookings = [], refetch: refetchBookings } = useBookings();
  const batchedAvailability = useBatchedAvailability(propertyIds);
  const { blockDate, unblockDate } = useBlockDate();

  // Generar días del mes actual y 2 meses siguientes (3 meses total)
  const days =  useMemo(() => {
    const start = startOfMonth(currentMonth);
    const end = endOfMonth(addMonths(currentMonth, 2)); // 3 meses total
    const allDays = eachDayOfInterval({ start, end });
    console.log('📅 Generated 3 months of days:', { 
      start: start.toDateString(), 
      end: end.toDateString(), 
      count: allDays.length 
    });
    return allDays;
  }, [currentMonth]);

  // Organizar reservaciones por propiedad
  const bookingsByProperty = useMemo(() => {
    const grouped: { [key: string]: any[] } = {};
    properties.forEach(property => {
      grouped[property.id] = bookings.filter(booking => booking.property_id === property.id);
    });
    console.log('📊 Bookings by property:', Object.keys(grouped).map(id => `${id}: ${grouped[id].length}`));
    return grouped;
  }, [bookings, properties]);

  // Funciones de utilidad
  const isDayBooked = (day: Date, propertyId: string) => {
    const dayStr = format(day, 'yyyy-MM-dd');
    const isBooked = (bookingsByProperty[propertyId] || []).some(booking => {
      const checkIn = booking.check_in;
      const checkOut = booking.check_out;
      return dayStr >= checkIn && dayStr < checkOut;
    });
    
    return isBooked;
  };

  const isDateBlocked = (date: Date, propertyId: string) => {
    const propertyAvailability = batchedAvailability[propertyId];
    if (!propertyAvailability || propertyAvailability.isLoading) return false;
    
    const normalizeDate = (d: Date) => {
      const normalized = new Date(d);
      normalized.setHours(0, 0, 0, 0);
      return normalized;
    };
    
    const normalizedCheckDate = normalizeDate(date);
    const isBlocked = propertyAvailability.unavailableDates.some(blockedDate => {
      const normalizedBlockedDate = normalizeDate(blockedDate);
      return normalizedCheckDate.getTime() === normalizedBlockedDate.getTime();
    });
    
    return isBlocked;
  };

  // Función para refrescar datos después de cambios - MEJORADA
  const handleDateUpdated = () => {
    console.log('🔄 MultiPropertyCalendar: Refreshing calendar data after date update');
    refetchBookings();
    
    // Refrescar availability data con mayor agresividad
    Object.entries(batchedAvailability).forEach(([propertyId, availability]) => {
      if (availability && typeof availability === 'object' && 'refetch' in availability) {
        console.log(`🔄 Force refreshing availability for property ${propertyId}`);
        (availability as any).refetch?.();
      }
    });
    
    // Force a re-render más agresivo
    setTimeout(() => {
      setCurrentMonth(prevMonth => new Date(prevMonth.getTime()));
    }, 100);
  };

  // Handler para añadir reservación
  const handleAddReservation = (date: Date, property: Property) => {
    console.log('📅 Adding reservation for:', date, property.id);
    setSelectedDateForReservation(date);
    setSelectedPropertyForReservation(property.id);
    setShowBookingFormModal(true);
  };

  // Handler para bloquear/desbloquear fecha
  const handleBlockDate = (date: Date, property: Property) => {
    const isBlocked = isDateBlocked(date, property.id);
    
    if (isBlocked) {
      console.log('🔓 Unblocking date:', date, property.id);
      unblockDate({ propertyId: property.id, date });
    } else {
      console.log('🔒 Blocking date:', date, property.id);
      blockDate({ propertyId: property.id, date });
    }
    
    // Refrescar datos después del cambio
    setTimeout(() => {
      handleDateUpdated();
    }, 500);
  };

  // Handlers existentes
  const handleDayClick = (day: Date, property: Property) => {
    console.log(`📅 MultiPropertyCalendar day clicked: ${day.toDateString()} for property ${property.id} (Admin: ${isAdminContext})`);
  };

  const handleBookingClick = (booking: any) => {
    console.log('🏨 Booking clicked:', booking.id);
    setSelectedBooking(booking);
    setShowBookingModal(true);
  };

  const handleCancelBooking = async (bookingId: string) => {
    console.log('❌ Canceling booking:', bookingId);
    setShowBookingModal(false);
    handleDateUpdated();
  };

  const handleBookingCreated = () => {
    console.log('✅ Booking created successfully');
    setShowBookingFormModal(false);
    setSelectedDateForReservation(null);
    setSelectedPropertyForReservation('');
    handleDateUpdated();
  };

  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

  // Configuración de layout - VALORES ORIGINALES MANTENIDOS
  const cellWidth = 60;
  const rowHeight = 45;
  const labelColumnWidth = 160;
  const today = new Date();

  return (
    <TooltipProvider>
      <div className="bg-white rounded-lg shadow-sm border">
        {/* Header con navegación */}
        <div className="border-b p-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">
            Calendario
          </h2>
          <div className="flex items-center space-x-4">
            <Button variant="outline" size="sm" onClick={prevMonth}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-lg font-medium min-w-[180px] text-center">
              {format(currentMonth, 'MMMM yyyy', { locale: es })}
            </span>
            <Button variant="outline" size="sm" onClick={nextMonth}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* NUEVA ESTRUCTURA: Layout principal con columna fija y área scrolleable */}
        <div className="flex h-[500px]">
          {/* Columna izquierda FIJA - Propiedades */}
          <div className="flex flex-col" style={{ width: labelColumnWidth }}>
            {/* Header fijo para la columna de propiedades */}
            <div className="h-12 border-b border-r bg-gray-50 flex items-center justify-center">
              <span className="text-sm font-semibold text-gray-700">Propiedades</span>
            </div>
            
            {/* Header de días fijo */}
            <div className="h-10 border-b border-r bg-gray-50"></div>
            
            {/* Labels de propiedades con scroll sincronizado */}
            <div className="flex-1 border-r bg-gray-50/50 overflow-hidden">
              <PropertyLabelsColumn 
                properties={properties}
                rowHeight={rowHeight}
                labelColumnWidth={labelColumnWidth}
              />
            </div>
          </div>

          {/* Área derecha SCROLLEABLE - Headers y Grid */}
          <div 
            ref={scrollableAreaRef}
            className="flex-1 overflow-auto"
            style={{ scrollbarWidth: 'thin' }}
          >
            <div style={{ width: days.length * cellWidth }}>
              {/* Headers de meses */}
              <CalendarHeader 
                days={days}
                cellWidth={cellWidth}
                labelColumnWidth={0} // No necesita espacio ya que está en el área scrolleable
              />
              
              {/* Headers de días */}
              <CalendarDayHeaders 
                days={days}
                cellWidth={cellWidth}
                labelColumnWidth={0} // No necesita espacio ya que está en el área scrolleable
              />
              
              {/* Grid del calendario */}
              <CalendarGrid 
                days={days}
                properties={properties}
                bookingsByProperty={bookingsByProperty}
                isDayBooked={isDayBooked}
                isDateBlocked={isDateBlocked}
                today={today}
                cellWidth={cellWidth}
                rowHeight={rowHeight}
                onDayClick={handleDayClick}
                onBookingClick={handleBookingClick}
                isAdminContext={isAdminContext}
                onDateUpdated={handleDateUpdated}
                onAddReservation={handleAddReservation}
                onBlockDate={handleBlockDate}
              />
            </div>
          </div>
        </div>

        {/* Modal de detalles de reservación */}
        <ReservationDetailModal 
          isOpen={showBookingModal}
          onClose={() => setShowBookingModal(false)}
          booking={selectedBooking}
          onCancelBooking={handleCancelBooking}
        />

        {/* Modal de formulario de reservación */}
        <BookingFormModal
          isOpen={showBookingFormModal}
          onClose={() => setShowBookingFormModal(false)}
          preselectedPropertyId={selectedPropertyForReservation}
          preselectedCheckInDate={selectedDateForReservation}
          onBookingCreated={handleBookingCreated}
        />
      </div>
    </TooltipProvider>
  );
};

export default MultiPropertyCalendar;
